"use client";

import { useCartQuery } from "@/generated/graphql";
import { ReactNode, Suspense, useState } from "react";
import { CartRefCode } from "./CartRefCode";
import { CookieEnum, getCookie } from "@/shared/utils/cookie";
import { CartContext } from "./CartContext";
import { useIsBrowser } from "@/hooks/browser-only";
import { getIsPaymentDomain } from "@/shared/utils/shinglePageCheckoutFunctions";
import { setCookie } from "nookies";
import { useParams } from "@/i18n/i18n-navigation";

interface CartProviderProps {
  hostname: string;
  lang: string;
  storeId: string;
  cartId?: string;
  children: ReactNode;
}

export function CartProvider({
  children,
  lang,
  storeId,
  cartId,
  hostname,
}: CartProviderProps) {
  const sessionId = getCookie(CookieEnum.SESSION_ID);
  const isBrowser = useIsBrowser();
  const isPaymentDomain = getIsPaymentDomain(hostname);
  const [isCalled, setIsCalled] = useState(false);
  const params = useParams();
  const queryCartId = params.cartId as string;
  // this will be deferred and called only on browser
  const { data, loading } = useCartQuery({
    variables: { storeId, cartId: queryCartId || cartId },

    context: {
      headers: { locale: lang, sessionId },
      // fetchOptions: { next: { revalidate: 0 } },
    },
    onCompleted: (data) => {
      if (isPaymentDomain) {
        setCookie(null, "sessionId", data?.customerQueries?.cart?.sessionId!, {
          maxAge: 30 * 24 * 60 * 20,
          path: "/",
        });
      }
      setIsCalled(true);
    },
    skip:
      (isPaymentDomain && !cartId) ||
      !storeId ||
      isCalled ||
      (!sessionId && !cartId && !queryCartId) ||
      queryCartId?.startsWith("Order_"),
  });

  const isFetching = loading || !isBrowser;

  return (
    <CartContext
      isFetching={isFetching}
      initialCartData={data?.customerQueries?.cart!}
    >
      <Suspense>
        <CartRefCode />
      </Suspense>
      {children}
    </CartContext>
  );
}
